import { BASE_CHAIN_ID } from "@breakout/shared"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@breakout/ui/components/DropdownMenu"
import { useToast } from "@breakout/ui/components/Toast"
import { cn, formatUsd } from "@breakout/ui/utils"
import { useFundWallet, usePrivy, useWallets } from "@privy-io/react-auth"
import { Link, useMatchRoute } from "@tanstack/react-router"
import { Banknote, Check, ChevronDown, Copy, KeyRound, Laptop, Moon, Sun, User } from "lucide-react"
import { motion } from "motion/react"
import type { ReactNode } from "react"
import { useCallback, useEffect, useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { useUserPortfolioStats } from "../../hooks/indexer/useUserPortfolioStats"
import { useAuthState } from "../../hooks/user/useAuthState"
import { useAutoFundOnFirstLogin } from "../../hooks/wallet/useAutoFundOnFirstLogin"
import { useUsdcBalance } from "../../hooks/wallet/useUsdcBalance"
import { breakoutChain, useBreakoutConfig } from "../../lib/providers"
import { findEmbeddedWallet } from "../../lib/wallet"
import { HowItWorksModal } from "../modals/HowItWorksModal"
import { RegionalRestrictionModal } from "../modals/RegionalRestrictionModal"
import { useTheme } from "../theme/ThemeProvider"
import { ThemeToggle } from "../theme/ThemeToggle"
import { ScrollToTopButton } from "../ui/ScrollToTopButton"
import { Skeleton } from "../ui/Skeleton"
import { XLogo } from "../ui/XLogo"

interface PageLayoutProps {
	children: ReactNode
}

export function PageLayout({ children }: PageLayoutProps) {
	return (
		<div className="flex min-h-dvh flex-col bg-bg-primary text-text-primary selection:bg-breakout-primary/30 selection:text-breakout-primary-hover">
			{/* Skip to main content link - visible only on focus for keyboard users */}
			<a
				className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[var(--z-modal)] focus:rounded-lg focus:bg-gray-900 focus:px-4 focus:py-2 focus:font-medium focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
				href="#main-content"
			>
				Skip to main content
			</a>
			<RegionalRestrictionModal />
			<Header />
			<motion.main
				animate={{ opacity: 1, y: 0 }}
				className="w-full flex-1"
				id="main-content"
				initial={{ opacity: 0, y: 10 }}
				tabIndex={-1}
				transition={{ duration: 0.15, ease: "easeOut" }}
			>
				{children}
			</motion.main>
			<Footer />
			<ScrollToTopButton />
		</div>
	)
}

// =============================================================================
// Subcomponents (single-use, colocated)
// =============================================================================

// Local storage key for remembering auth state
const AUTH_HINT_KEY = "breakout_auth_hint"

// Header container classes - stable reference
const HEADER_CONTAINER_CLASSES =
	"relative mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8"

interface HeaderProps {
	className?: string
}

// Prevents flash by showing appropriate skeleton while Privy initializes
function getAuthHint(): boolean {
	if (typeof window === "undefined") {
		return false
	}
	try {
		return localStorage.getItem(AUTH_HINT_KEY) === "true"
	} catch {
		return false
	}
}

function setAuthHint(wasLoggedIn: boolean): void {
	if (typeof window === "undefined") {
		return
	}
	try {
		if (wasLoggedIn) {
			localStorage.setItem(AUTH_HINT_KEY, "true")
		} else {
			localStorage.removeItem(AUTH_HINT_KEY)
		}
	} catch {
		// Ignore localStorage errors
	}
}

function Header({ className }: HeaderProps) {
	// 1. Context/store access
	const matchRoute = useMatchRoute()
	const isTimelinePage = !!matchRoute({ to: "/timeline" })
	const { ready, login, logout: privyLogout, exportWallet } = usePrivy()
	const { wallets } = useWallets()
	const { fundWallet } = useFundWallet()
	const { user, evmWallet, isAuthenticated, isLoading: isAuthLoading } = useAuthState()
	const { showToast } = useToast()
	const { theme, setTheme } = useTheme()
	const { chainId } = useBreakoutConfig()

	// Local state - safely convert wallet address to checksum Address type
	const walletAddress =
		evmWallet?.address && isAddress(evmWallet.address) ? getAddress(evmWallet.address) : undefined
	const [wasLoggedIn] = useState(() => getAuthHint())
	const [copied, setCopied] = useState(false)

	// Get embedded wallet address for funding (only embedded wallets can be funded via Privy)
	const embeddedWallet = findEmbeddedWallet(wallets)
	const embeddedWalletAddress: Address | null =
		embeddedWallet?.address && isAddress(embeddedWallet.address)
			? getAddress(embeddedWallet.address)
			: null

	// Auto-open fund wallet modal on first login (only on mainnet)
	useAutoFundOnFirstLogin({
		ready,
		isAuthenticated,
		embeddedWalletAddress,
		chainId,
	})

	// 2. Data queries
	const { data: balanceData } = useUsdcBalance(walletAddress ? { walletAddress } : null)
	const isZeroBalance = balanceData?.balance === 0n
	const cashBalance = isZeroBalance ? "Add cash" : (balanceData?.formattedBalance ?? "—")

	// Fetch portfolio stats (lightweight query - just stats, no positions)
	const { data: portfolioStats } = useUserPortfolioStats(
		walletAddress ? { userAddress: walletAddress } : null,
	)

	// 3. Effects
	// Update auth hint when auth state changes
	useEffect(() => {
		if (ready && !isAuthLoading) {
			setAuthHint(isAuthenticated)
		}
	}, [ready, isAuthLoading, isAuthenticated])

	// 4. Derived values
	// Portfolio value from backend stats
	const portfolioBalance = formatUsd(portfolioStats?.totalValue ?? 0)

	// Determine what to show in the auth section:
	// 1. If Privy not ready: show skeleton if user was logged in, show login button otherwise
	// 2. If Privy ready but auth still loading (Convex sync): show skeleton
	// 3. If fully ready: show appropriate state
	const isInitializing = !ready || isAuthLoading
	const showSkeleton = isInitializing && wasLoggedIn
	const showProfile = !isInitializing && isAuthenticated

	// 5. Event handlers
	// Handle logout - clear auth hint immediately to prevent flash on next load
	const logout = useCallback(async () => {
		setAuthHint(false)
		await privyLogout()
	}, [privyLogout])

	// Copy address to clipboard
	const copyAddress = useCallback(async () => {
		if (!walletAddress) {
			return
		}
		await navigator.clipboard.writeText(walletAddress)
		setCopied(true)
		showToast("Address copied to clipboard", "success")
		setTimeout(() => setCopied(false), 2000)
	}, [walletAddress, showToast])

	// Open Privy fund wallet modal
	const openFundWallet = useCallback(async () => {
		if (!embeddedWalletAddress) {
			return
		}

		// Privy onramps only work on mainnet
		if (chainId !== BASE_CHAIN_ID) {
			showToast("Funding only works on mainnet", "info")
			return
		}

		try {
			await fundWallet({
				address: embeddedWalletAddress,
				options: { chain: breakoutChain }, // Explicitly fund on Base
			})
		} catch (error) {
			console.warn("[FundWallet] Error opening fund wallet:", error)
		}
	}, [embeddedWalletAddress, fundWallet, chainId, showToast])

	return (
		<header
			className={cn(
				"sticky top-0 z-[var(--z-header)] min-h-[72px] border-border-subtle border-b bg-bg-glass backdrop-blur-md",
				className,
			)}
		>
			<div className={HEADER_CONTAINER_CLASSES}>
				{/* Logo */}
				<Link
					aria-label="Breakout home"
					className="flex items-center gap-3 rounded-lg transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-[0.98]"
					to="/"
				>
					<img
						alt=""
						className="size-12 rounded-2xl"
						decoding="async"
						height={48}
						loading="eager"
						src="/icon-192.png"
						width={48}
					/>
					<div>
						<h1 className="text-balance font-bold font-display text-text-primary text-xl">
							Breakout
						</h1>
						<p className="text-sm text-text-muted">Trade attention</p>
					</div>
				</Link>

				{/* Centered Timeline link — desktop only, hidden on /timeline */}
				{!isTimelinePage && (
					<Link
						className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-2 rounded-lg px-3.5 py-2 font-medium text-base text-text-secondary transition-colors hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand sm:inline-flex"
						to="/timeline"
					>
						<XLogo aria-hidden="true" className="size-[18px]" />
						<span>CT Bangers</span>
					</Link>
				)}

				{/* Auth Section */}
				<div className="flex items-center gap-2 sm:gap-3">
					{/* How it works trigger */}
					<HowItWorksModal />

					{/* Theme Toggle - only show when not logged in (logged in users use menu) */}
					{!showProfile && !showSkeleton && <ThemeToggle />}

					{/* Show balance stats for authenticated users or skeleton while loading */}
					{(showProfile || showSkeleton) && (
						<div className="hidden items-center gap-3 lg:flex">
							<div className="flex items-center rounded-xl bg-bg-tertiary ring-1 ring-border-subtle">
								<Link
									aria-label="View portfolio"
									className="border-border-highlight border-r px-3 py-1.5 text-right transition-colors hover:bg-bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand"
									to="/profile"
								>
									<p className="font-bold text-[10px] text-text-muted uppercase">Portfolio</p>
									{showSkeleton ? (
										<Skeleton className="ml-auto h-5 w-14 bg-border-highlight" />
									) : (
										<p className="font-bold font-mono text-breakout-brand text-sm tabular-nums">
											{portfolioBalance}
										</p>
									)}
								</Link>
								<button
									aria-label="Add cash to wallet"
									className="cursor-pointer px-3 py-1.5 text-right transition-colors hover:bg-bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand disabled:cursor-default disabled:hover:bg-transparent"
									disabled={!embeddedWalletAddress}
									onClick={() => void openFundWallet()}
									type="button"
								>
									<p className="font-bold text-[10px] text-text-muted uppercase">Cash</p>
									{showSkeleton ? (
										<Skeleton className="ml-auto h-5 w-14 bg-border-highlight" />
									) : (
										<p
											className={cn(
												"font-bold font-mono text-sm tabular-nums",
												isZeroBalance ? "text-breakout-brand" : "text-text-primary",
											)}
										>
											{cashBalance}
										</p>
									)}
								</button>
							</div>
						</div>
					)}
					{/* Profile button skeleton while loading for previously logged-in users */}
					{showSkeleton ? (
						<div className="flex items-center gap-2 rounded-xl border border-border px-2 py-1.5 lg:gap-3 lg:px-3 lg:py-2">
							<Skeleton className="size-8 rounded-full bg-bg-tertiary lg:size-9" />
							<Skeleton className="hidden h-4 w-16 bg-bg-tertiary lg:block" />
							<Skeleton className="size-4 bg-bg-tertiary" />
						</div>
					) : showProfile ? (
						<DropdownMenu modal={false}>
							<DropdownMenuTrigger asChild>
								<button
									aria-label="Account menu"
									className="flex min-h-[40px] items-center gap-2 rounded-xl border border-border bg-bg-card px-2 py-1.5 text-base text-text-secondary transition-all hover:border-breakout-brand/50 hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand lg:min-h-[44px] lg:gap-3 lg:px-3 lg:py-2"
									data-testid="user-menu"
									type="button"
								>
									<div className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary ring-1 ring-border-highlight lg:size-9">
										{user?.avatarUrl ? (
											<img
												alt=""
												className="size-full object-cover"
												decoding="async"
												height={36}
												loading="eager"
												referrerPolicy="no-referrer"
												src={user.avatarUrl}
												width={36}
											/>
										) : (
											<User aria-hidden="true" className="size-4 text-text-muted" />
										)}
									</div>
									<span className="hidden font-medium text-text-primary lg:inline">
										{user?.username ? `@${user.username}` : "Account"}
									</span>
									<ChevronDown aria-hidden="true" className="size-4 text-text-muted" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align="end"
								className="z-[var(--z-dropdown)] w-48 border border-border-subtle bg-bg-card p-1 shadow-xl lg:w-56"
							>
								<DropdownMenuItem
									asChild
									className="cursor-pointer rounded-lg focus:bg-bg-secondary focus:text-text-primary"
								>
									<Link to="/profile">Profile</Link>
								</DropdownMenuItem>
								{embeddedWalletAddress && (
									<DropdownMenuItem
										aria-label="Add cash"
										className="cursor-pointer rounded-lg focus:bg-bg-secondary focus:text-text-primary"
										onClick={() => void openFundWallet()}
									>
										<Banknote aria-hidden="true" className="mr-2 size-4 text-text-muted" />
										Add cash
									</DropdownMenuItem>
								)}
								{walletAddress && (
									<DropdownMenuItem
										className="cursor-pointer rounded-lg focus:bg-bg-secondary focus:text-text-primary"
										onClick={() => void copyAddress()}
									>
										{copied ? (
											<Check aria-hidden="true" className="mr-2 size-4 text-breakout-brand" />
										) : (
											<Copy aria-hidden="true" className="mr-2 size-4 text-text-muted" />
										)}
										{copied ? "Copied!" : "Copy address"}
									</DropdownMenuItem>
								)}
								<DropdownMenuItem
									className="cursor-pointer rounded-lg focus:bg-bg-secondary focus:text-text-primary"
									onClick={() => void exportWallet()}
								>
									<KeyRound aria-hidden="true" className="mr-2 size-4 text-text-muted" />
									Export wallet
								</DropdownMenuItem>
								<DropdownMenuItem
									asChild
									className="cursor-pointer rounded-lg focus:bg-bg-secondary focus:text-text-primary"
								>
									<a
										href="https://showtime-xyz.notion.site/Legal-Public-c407e36eb7cd414ca190245ca8621e68"
										rel="noopener noreferrer"
										target="_blank"
									>
										Terms
									</a>
								</DropdownMenuItem>
								<DropdownMenuSeparator className="my-1 bg-border-subtle" />
								<div className="flex items-center justify-between px-3 py-2">
									<span className="text-sm text-text-secondary">Theme</span>
									<div className="flex items-center gap-1 rounded-lg bg-bg-tertiary p-1 ring-1 ring-border-subtle">
										<button
											aria-label="Light theme"
											className={cn(
												"rounded-md p-1.5 transition-all active:scale-95",
												theme === "light"
													? "bg-bg-card text-text-primary shadow-sm ring-1 ring-border-subtle"
													: "text-text-muted hover:bg-bg-secondary hover:text-text-secondary",
											)}
											onClick={() => setTheme("light")}
											type="button"
										>
											<Sun aria-hidden="true" className="size-4" />
										</button>
										<button
											aria-label="Dark theme"
											className={cn(
												"rounded-md p-1.5 transition-all active:scale-95",
												theme === "dark"
													? "bg-bg-card text-text-primary shadow-sm ring-1 ring-border-subtle"
													: "text-text-muted hover:bg-bg-secondary hover:text-text-secondary",
											)}
											onClick={() => setTheme("dark")}
											type="button"
										>
											<Moon aria-hidden="true" className="size-4" />
										</button>
										<button
											aria-label="System theme"
											className={cn(
												"rounded-md p-1.5 transition-all active:scale-95",
												theme === "system"
													? "bg-bg-card text-text-primary shadow-sm ring-1 ring-border-subtle"
													: "text-text-muted hover:bg-bg-secondary hover:text-text-secondary",
											)}
											onClick={() => setTheme("system")}
											type="button"
										>
											<Laptop aria-hidden="true" className="size-4" />
										</button>
									</div>
								</div>
								<DropdownMenuSeparator className="my-1 bg-border-subtle" />
								<DropdownMenuItem
									className="cursor-pointer rounded-lg text-red-500 focus:bg-red-500/10 focus:text-red-600 dark:focus:text-red-400"
									onClick={() => void logout()}
								>
									Log out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<button
							className="min-h-[40px] rounded-xl bg-text-primary px-4 py-2 font-bold text-base text-bg-primary shadow-glow-sm transition-all hover:scale-[1.02] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-primary focus-visible:ring-offset-2 active:scale-[0.98] lg:min-h-[44px] lg:px-5 lg:py-2.5"
							onClick={login}
							type="button"
						>
							Log in
						</button>
					)}
				</div>
			</div>
		</header>
	)
}

function Footer() {
	return (
		<footer className="mt-auto border-border-subtle border-t py-4">
			<div className="mx-auto flex max-w-6xl items-center justify-center gap-4 px-4 text-text-muted text-xs sm:gap-6">
				<a
					className="transition-colors hover:text-text-secondary"
					href="https://showtime-xyz.notion.site/Breakout-app-FAQ-2eb656c42eea8069ac5efc6bb3643a11"
					rel="noopener noreferrer"
					target="_blank"
				>
					FAQ
				</a>
				<span className="text-border-highlight">·</span>
				<a
					className="transition-colors hover:text-text-secondary"
					href="https://showtime-xyz.notion.site/Legal-Public-c407e36eb7cd414ca190245ca8621e68"
					rel="noopener noreferrer"
					target="_blank"
				>
					Legal
				</a>
				<span className="text-border-highlight">·</span>
				<a
					aria-label="Follow Breakout on X"
					className="transition-colors hover:text-text-secondary"
					href="https://x.com/BreakoutApp"
					rel="noopener noreferrer"
					target="_blank"
				>
					𝕏
				</a>
			</div>
		</footer>
	)
}
